import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbtack, faCheck } from '@fortawesome/free-solid-svg-icons';
import { myDeskService } from '../../services/myDeskService';
import { useFlashMessage } from '../FlashMessage';
import './PinToDeskButton.css';

/**
 * "Pin to My Desk" for an entity screen header.
 *
 * The server decides whether the object may be pinned using the same
 * visibility rule as the screen itself, so this button only reflects state.
 * Renders nothing until the object id is known (create screens).
 */
const PinToDeskButton = ({ entityType, objectId, className = '' }) => {
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();
  const [pinned, setPinned] = useState(false);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setPinned(false);
    if (!objectId) return undefined;
    myDeskService.pinStatus(entityType, objectId)
      .then((result) => { if (!cancelled) { setPinned(!!result?.pinned); setReady(true); } })
      .catch((err) => {
        console.error('My Desk: pin status failed', err);
        if (!cancelled) setReady(true);
      });
    return () => { cancelled = true; };
  }, [entityType, objectId]);

  if (!objectId || !ready) return null;

  const handleClick = async () => {
    if (pinned) {
      history.push('/dashboard');
      return;
    }
    setBusy(true);
    try {
      await myDeskService.pin(entityType, objectId);
      setPinned(true);
      showSuccess('Pinned to My Workspace.');
    } catch (err) {
      console.error('My Desk: pin failed', err);
      const message = err?.response?.data?.error || 'This item could not be pinned.';
      showError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className={`mydesk-pin-btn ${pinned ? 'is-pinned' : ''} ${className}`}
      onClick={handleClick}
      disabled={busy}
      title={pinned ? 'Open My Workspace' : 'Pin to My Workspace'}
    >
      <FontAwesomeIcon icon={pinned ? faCheck : faThumbtack} />
      <span>{pinned ? 'Pinned to My Workspace' : 'Pin to My Workspace'}</span>
    </button>
  );
};

export default PinToDeskButton;
